import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { getAlertBus } from './alertBus';
import { InboxChannel } from './types';

/** One row of `notification_preferences` (plan §7). */
export interface NotificationPreferences {
  user_id: string;
  desktop_enabled: boolean;
  toasts_enabled: boolean;
  sound_enabled: boolean;
  channels: InboxChannel[];
  unclaimed_enabled: boolean;
  prompt_dismissed_at: string | null;
}

export type PreferencesPatch = Partial<Omit<NotificationPreferences, 'user_id'>>;

/**
 * Everyone's settings until they change one — the same defaults as the
 * table's. Desktop starts off only because Chrome needs a click to allow it.
 */
export function defaultPreferences(userId: string): NotificationPreferences {
  return {
    user_id: userId,
    desktop_enabled: false,
    toasts_enabled: true,
    sound_enabled: false,
    channels: ['sms', 'call', 'voicemail'],
    unclaimed_enabled: true,
    prompt_dismissed_at: null,
  };
}

/**
 * The user's alert settings: their row, else the defaults (alerts are on for
 * all staff). Null only until the first read comes back.
 *
 * Every tab reads its own copy; a change in one tab tells the others to
 * re-read, so the leader tab always routes by the current settings.
 */
export function useNotificationPreferences(userId: string | null): {
  prefs: NotificationPreferences | null;
  update: (patch: PreferencesPatch) => Promise<void>;
} {
  const [prefs, setPrefsState] = useState<NotificationPreferences | null>(null);
  // The latest settings, synchronously: two changes in a row (turn desktop on,
  // then dismiss the prompt) must each build on the other, not on the render
  // they were clicked from — the upsert writes the whole row.
  const latest = useRef<NotificationPreferences | null>(null);
  const setPrefs = useCallback((next: NotificationPreferences) => {
    latest.current = next;
    setPrefsState(next);
  }, []);

  const read = useCallback(async () => {
    if (!userId) return;
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (!error) setPrefs((data as NotificationPreferences | null) ?? defaultPreferences(userId));
  }, [userId, setPrefs]);

  useEffect(() => {
    void read();
    return getAlertBus().subscribe((msg) => {
      if (msg.type === 'prefs') void read();
    });
  }, [read]);

  const update = useCallback(
    async (patch: PreferencesPatch) => {
      if (!userId) return;
      const next = { ...(latest.current ?? defaultPreferences(userId)), ...patch };
      setPrefs(next);
      // Upsert: the first change is what creates the user's row.
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({ ...next, updated_at: new Date().toISOString() });
      if (error) {
        await read();
        throw error;
      }
      getAlertBus().post({ type: 'prefs' });
    },
    [userId, read, setPrefs],
  );

  return { prefs, update };
}

/** The browser's notification permission, or 'unsupported'. */
export function desktopPermission(): NotificationPermission | 'unsupported' {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

/**
 * Ask for the desktop permission. Must run inside a click handler — Chrome
 * ignores (and eventually blocks) requests that aren't user-initiated.
 */
export async function requestDesktopPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}
